/* eslint-disable react-hooks/exhaustive-deps */
import { useRollbar } from '@rollbar/react';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppLoading, FormModal } from '@components';
import { CounselorForm, OffRamp } from '@components';
import { useActivityTimer } from '@hooks';
import { generateModules, GTMService, loadVariantConfig } from '@services';
import { useStore } from '@stores';
import { sendEventToHost } from '@utils';

const App = observer(() => {
  const {
    commonStore,
    initializeStore,
    formStore,
    partnerStore: { partner },
    userStore: { user },
  } = useStore();
  const rollbar = useRollbar();

  const { appLoading } = commonStore;
  const variant = initializeStore.getVariant();
  const {
    gaClientId,
    consentCookie,
    isLoading: isInitializing,
  } = initializeStore;
  const { inactivityTimer, firebaseLoading } = useActivityTimer();
  const {
    i18n: { language },
  } = useTranslation();
  const { options } = initializeStore;
  const [variantConfigFailed, setVariantConfigFailed] = useState(false);

  useEffect(() => {
    // The session cannot proceed without a form config. Report it once with
    // enough context to identify the integration, and hand the host the 'error'
    // event it uses to close the embed.
    const reportStartupFailure = (message: string, error: Error) => {
      rollbar.error(message, error, {
        variant,
        requestingHost: options.requestingHost,
        pid: options.pid,
      });
      sendEventToHost('error', { data: { error } });
    };

    const updateVariant = async () => {
      try {
        const [config, modules] = await Promise.all([
          loadVariantConfig(variant),
          generateModules(variant),
        ]);
        if (!config || !modules) {
          throw new Error('Variant config is empty.');
        }
        setVariantConfigFailed(false);
        formStore.setFormConfig(config);
        formStore.setModuleElements(modules);
      } catch (error) {
        setVariantConfigFailed(true);
        reportStartupFailure(
          'App: Variant config could not be loaded.',
          error as Error
        );
      }
    };

    if (isInitializing) {
      return;
    }

    if (variant) {
      updateVariant();
    } else {
      reportStartupFailure(
        'App: Variant is required but not specified.',
        new Error('Variant is required but not specified.')
      );
    }

    return () => {
      formStore.clearFormStore();
    };
  }, [variant, formStore, isInitializing]);

  useEffect(() => {
    if (!firebaseLoading) {
      inactivityTimer();
    }
  }, [firebaseLoading, inactivityTimer]);

  useEffect(() => {
    const initializeGTM = async () => {
      if (user && partner && !firebaseLoading) {
        await GTMService.getInstance().initializeDataLayer(
          user,
          partner,
          options.language ?? language,
          variant,
          options.requestingHostLong,
          gaClientId,
          consentCookie,
          options.introUI
        );
      }
    };
    initializeGTM();
  }, [user, partner, firebaseLoading]);

  // Without a usable variant config, CounselorForm would fall through to its
  // "Module Not Found" state. The host has been notified via the 'error' event
  // and is responsible for closing the embed.
  if (!isInitializing && (!variant || variantConfigFailed)) {
    return null;
  }

  return (
    <FormModal hideHeaderFooter={appLoading}>
      <Suspense>
        {appLoading ? (
          <AppLoading />
        ) : (
          <>
            <OffRamp />
            <CounselorForm />
          </>
        )}
      </Suspense>
    </FormModal>
  );
});

export default App;
